import { Link, useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import {
  Linkedin,
  Instagram,
  Facebook,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import "../styles/Footer.scss";
import PrivacyPolicy from '../../src/lib/pdf/PrivacyPolicy.pdf'

const Footer = () => {
  const location = useLocation();
  const isPricingPage = location.pathname === "/pricing";
  const isReportPage = location.pathname === "/report";

  // Function to scroll to element with offset and manage focus
  const scrollWithOffset = (el: HTMLElement) => {
    const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
    const yOffset = -80; // Adjust based on navbar height
    
    // First scroll to position
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' }); 
    
    // After scrolling completes, set focus to the element for accessibility
    setTimeout(() => {
      el.setAttribute('tabindex', '-1');
      el.focus({ preventScroll: true });
      
      // Remove tabindex after focus to avoid navigation issues
      el.addEventListener('blur', () => {
        el.removeAttribute('tabindex');
      }, { once: true });
    }, 500);
  };

  return (
    <footer className="footer2" role="contentinfo" aria-labelledby="footer-heading">
      <h2 id="footer-heading" className="sr-only">Footer</h2>
      <div className="footer2__container">
        <div className="footer2__main">
          {/* Company & Logo Section */}
          <section className="footer2__company" aria-labelledby="company-section">
            <span id="company-section" className="sr-only">About the company</span>
            <div className="footer2__logo-wrapper">
              <img
                src="/lovable-uploads/f0f0f6e4-80a7-4016-a26d-c6d7c0a73134.png"
                alt="MNB Solutions"
                className="footer2__logo"
              />
            </div>
            <p className="footer2__company-description">
              Ensuring digital accessibility with AccessiBridge, a powerful
              AI-enhanced tool that scans websites for compliance with EAA, BFSG
              and WCAG standards.
            </p>
            <nav aria-label="Social media links" className="footer2__social">
              <ul className="footer2__social-links">
                <li>
                <a
                  href="https://www.linkedin.com/company/mnb-solutions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer2__social-link"
                    aria-label="LinkedIn profile"
                >
                  <Linkedin size={18} aria-hidden="true" />
                </a>
                </li>
                <li>
                <a
                  href="https://www.instagram.com/mnbsolutions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer2__social-link"
                    aria-label="Instagram profile"
                >
                  <Instagram size={18} aria-hidden="true" />
                </a>
                </li>
                <li>
                <a
                  href="https://www.facebook.com/MNBSolutions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer2__social-link"
                    aria-label="Facebook page"
                >
                  <Facebook size={18} aria-hidden="true" />
                </a>
                </li>
              </ul>
            </nav>
          </section>

          {/* Quick Links */}
          <nav aria-labelledby="quick-links-heading" className="footer2__quick-links">
            <h2 id="quick-links-heading" className="footer2__heading">
              Quick Links
            </h2>
            <ul className="footer2__link-list">
              {[
                "Home",
                "Digital Accessibility",
                "EAA Countdown",
                "Who We Serve",
                "Compliance Audit",
                "Contact",
              ].map((item) => {
                const sectionId = item.toLowerCase().replace(/\s+/g, "-");
                
                return (
                  <li key={item} className="footer2__link-item">
                    <HashLink
                      to={`/#${sectionId}`}
                      className="footer2__link"
                      scroll={scrollWithOffset}
                    >
                      {item}
                    </HashLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-labelledby="legal-heading" className="footer2__legal">
            <h2 id="legal-heading" className="footer2__heading">
              Legal
            </h2>
            <ul className="footer2__link-list">
              <li className="footer2__link-item">
                <a
                  href={PrivacyPolicy}
                  className="footer2__link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Privacy Policy (PDF document opens in new tab)"
                >
                  Privacy Policy
                </a>
              </li>
              <li className="footer2__link-item">
                <a
                  href="https://www.mnb.solutions/legal/imprint/"
                  className="footer2__link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Imprint page (opens in new tab)"
                >
                  Imprint
                </a>
              </li>
            </ul>
          </nav>

          {/* Contact Info */}
          <section className="footer2__contact" aria-labelledby="contact-heading">
            <h2 id="contact-heading" className="footer2__heading">
              Contact Us
            </h2>
            <address className="footer2__address">
              <div className="footer2__address-item">
                <MapPin
                  className="footer2__address-icon"
                  size={18}
                  aria-hidden="true"
                />
                <span className="footer2__address-text">
                  MNB s.r.o.
                  <br />
                  Karpatská 8<br />
                  811 05 Bratislava, Slovakia
                </span>
              </div>
            </address>
          </section>
        </div>

        {/* Copyright Section */}
        <div className="footer2__copyright" role="contentinfo">
          <p className="footer2__copyright-text">
            &copy; {new Date().getFullYear()} MNB Solutions. All rights
            reserved.
          </p>
          <p className="footer2__copyright-powered">Powered by AccessiBridge™</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 